import {
  useFeatureFlags,
  useFeatureFlagsProviderContext,
} from '@brightdrop/feature-flags-client';
import {
  isSupportedLocale,
  SUPPORTED_LOCALES,
  type SupportedLocale,
} from '@brightdrop/localization-core';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { LOCALIZATION_FEATURE_FLAG } from '~/common/models/featureFlags.model';
import {
  selectProfile,
  selectProfileUserPreferences,
} from '~/store/profile/profileSlice.selectors';

import { IS_CONSOLE_ENV } from './consoleEnv';
import {
  clearStored,
  readStored,
  setGateOpen,
  setStored,
} from './overrideStore';
import useLanguageOverride from './useLanguageOverride';

/** The console command for switching the UI language on dev and UAT. */
export type CspLanguageConsole = {
  /** Switches the UI to a supported locale for this tab. */
  set: (locale: string) => void;
  /** Returns to the account's own language. */
  clear: () => void;
  /** Prints and returns the active language and the stored override. */
  current: () => { language: string; override: SupportedLocale | null };
  /** Prints and returns the supported locales. */
  list: () => SupportedLocale[];
};

declare global {
  var cspLanguage: CspLanguageConsole | undefined;
}

/**
 * Registers `cspLanguage` in the browser console on dev and UAT while the localization feature
 * flag is on for the signed-in user, and opens the gate that lets a stored console language
 * apply. Renders nothing. Elsewhere the command is never defined and a stored language is ignored.
 */
const LanguageOverrideConsole = (): null => {
  const { getFlag } = useFeatureFlags();
  const { isUserContextIdentified } = useFeatureFlagsProviderContext();
  const { i18n } = useTranslation();
  const userPreferences = useSelector(selectProfileUserPreferences);
  const profileId = useSelector(selectProfile)?.id;
  const override = useLanguageOverride();
  const announced = useRef(false);
  const wasUserContextIdentified = useRef(false);
  const [identifiedProfileId, setIdentifiedProfileId] = useState<string>();
  const profileLocale = userPreferences?.locale;

  // Recorded only as an identify completes. On the render a different user arrives, the flag
  // still reads identified for the previous one, so recording then would credit the new user
  // with the old user's flags.
  useEffect(() => {
    if (isUserContextIdentified && !wasUserContextIdentified.current) {
      setIdentifiedProfileId(profileId);
    }
    wasUserContextIdentified.current = isUserContextIdentified;
  }, [isUserContextIdentified, profileId]);

  // Until the user context is identified, the flag can still answer for the previous user. The
  // same user identified again (a saved language preference, say) keeps the gate open: closing it
  // would switch the language back and forth, resetting the API caches each time. The override
  // stands in for the account's language, so it also waits for that language: without it the
  // `locale` cookie has nothing to be restored to, and signed-out pages keep their own picker.
  const isContextIdentified =
    isUserContextIdentified ||
    (Boolean(profileId) && identifiedProfileId === profileId);
  const isGateOpen =
    IS_CONSOLE_ENV &&
    isContextIdentified &&
    Boolean(profileLocale) &&
    Boolean(getFlag(LOCALIZATION_FEATURE_FLAG));

  useEffect(() => {
    if (!isGateOpen) {
      setGateOpen(false);
      return;
    }

    globalThis.cspLanguage = {
      set: (locale) => {
        if (!isSupportedLocale(locale)) {
          console.error(
            `cspLanguage: "${locale}" is not supported. Use one of: ${SUPPORTED_LOCALES.join(', ')}`
          );
          return;
        }
        setStored(locale);
      },
      clear: clearStored,
      current: () => {
        const state = { language: i18n.language, override: readStored() };
        console.info('cspLanguage:', state);
        return state;
      },
      list: () => {
        const locales = [...SUPPORTED_LOCALES];
        console.info('cspLanguage:', locales.join(', '));
        return locales;
      },
    };
    setGateOpen(true);

    return () => {
      delete globalThis.cspLanguage;
      setGateOpen(false);
    };
  }, [isGateOpen, i18n]);

  useEffect(() => {
    if (!override || announced.current) return;
    announced.current = true;
    console.info(
      `cspLanguage: the UI language is set to ${override} for this tab. Run cspLanguage.clear() to return to the account's language.`
    );
  }, [override]);

  // i18next writes the `locale` cookie on every language change. The override is for this tab
  // only, so the cookie keeps naming the account's language and nothing outlives the tab.
  useEffect(() => {
    if (!override || !profileLocale || i18n.language !== override) return;
    i18n.services.languageDetector?.cacheUserLanguage?.(profileLocale);
  }, [override, profileLocale, i18n, i18n.language]);

  return null;
};

export default LanguageOverrideConsole;
